import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  value: string;          // YYYY-MM-DD
  onChange: (v: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

const DAYS_SHORT  = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const MONTHS_LONG = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const MONTHS_SH   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function parseDateStr(s: string): { y: number; mo: number; d: number } | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, mo, d] = s.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  if (isNaN(dt.getTime())) return null;
  return { y, mo, d };
}

function toYMD(y: number, mo: number, d: number): string {
  return `${y}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

/** JS getDay() → Mon=0…Sun=6 */
function jsDowToMonFirst(dow: number) { return (dow + 6) % 7; }

function buildGrid(y: number, mo: number): ({ d: number; mo: number; y: number; cur: boolean })[] {
  const firstDow = jsDowToMonFirst(new Date(y, mo - 1, 1).getDay());
  const daysInMonth = new Date(y, mo, 0).getDate();
  const cells: { d: number; mo: number; y: number; cur: boolean }[] = [];

  // Days from previous month
  const prevMo = mo === 1 ? 12 : mo - 1;
  const prevY  = mo === 1 ? y - 1 : y;
  const daysInPrev = new Date(prevY, prevMo, 0).getDate();
  for (let i = firstDow - 1; i >= 0; i--) {
    cells.push({ d: daysInPrev - i, mo: prevMo, y: prevY, cur: false });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ d, mo, y, cur: true });
  }

  // Next month fill to 6 rows
  const nextMo = mo === 12 ? 1 : mo + 1;
  const nextY  = mo === 12 ? y + 1 : y;
  let n = 1;
  while (cells.length % 7 !== 0 || cells.length < 35) {
    cells.push({ d: n++, mo: nextMo, y: nextY, cur: false });
  }
  return cells;
}

// ── Popup ──────────────────────────────────────────────────────────────────────
interface PopupProps {
  value: string; top: number; left: number;
  onPick: (v: string) => void; onClose: () => void;
}

const CalPopup = React.forwardRef<HTMLDivElement, PopupProps>(
  ({ value, top, left, onPick }, ref) => {
    const parsed = parseDateStr(value);
    const today  = new Date();
    const [viewY,  setViewY]  = useState(parsed?.y  ?? today.getFullYear());
    const [viewMo, setViewMo] = useState(parsed?.mo ?? today.getMonth() + 1);

    const prevMonth = () => { if (viewMo === 1) { setViewMo(12); setViewY(y => y-1); } else setViewMo(m => m-1); };
    const nextMonth = () => { if (viewMo === 12) { setViewMo(1); setViewY(y => y+1); } else setViewMo(m => m+1); };

    const grid = buildGrid(viewY, viewMo);

    const isSelected = (c: typeof grid[0]) =>
      parsed && c.y === parsed.y && c.mo === parsed.mo && c.d === parsed.d;

    const isToday = (c: typeof grid[0]) =>
      c.y === today.getFullYear() && c.mo === today.getMonth() + 1 && c.d === today.getDate();

    return (
      <div ref={ref} style={{
        position: 'fixed', top, left, zIndex: 1500,
        background: 'var(--glass-bg)', backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        border: '1px solid var(--glass-border-strong)',
        borderRadius: 14, boxShadow: '0 24px 64px rgba(0,0,0,.55)',
        padding: '12px 14px 10px',
        width: 248, boxSizing: 'border-box' as const,
        userSelect: 'none', WebkitUserSelect: 'none',
      }}>
        {/* Month navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <button onClick={prevMonth} style={navBtnSt}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '.01em' }}>
            {MONTHS_LONG[viewMo - 1]} {viewY}
          </span>
          <button onClick={nextMonth} style={navBtnSt}>›</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
          {DAYS_SHORT.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-disabled)', letterSpacing: '.06em', padding: '2px 0' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {grid.map((c, i) => {
            const sel = isSelected(c);
            const tod = isToday(c);
            return (
              <div key={i} onClick={() => onPick(toYMD(c.y, c.mo, c.d))} style={{
                textAlign: 'center', padding: '5px 2px', borderRadius: 7, cursor: 'pointer',
                fontSize: 12, fontWeight: sel ? 700 : 400,
                color: sel ? '#fff' : !c.cur ? 'var(--text-disabled)' : tod ? 'var(--accent)' : 'var(--text-primary)',
                background: sel ? 'var(--accent)' : tod && !sel ? 'var(--glass-hover)' : 'transparent',
                border: tod && !sel ? '1px solid var(--accent)' : '1px solid transparent',
                boxShadow: sel ? '0 2px 8px var(--accent-glow)' : 'none',
                transition: 'background .1s, color .1s',
              }}
              onMouseEnter={e => { if (!sel) (e.currentTarget as HTMLDivElement).style.background = 'var(--glass-hover)'; }}
              onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLDivElement).style.background = tod && !sel ? 'var(--glass-hover)' : 'transparent'; }}
              >
                {c.d}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
CalPopup.displayName = 'CalPopup';

const navBtnSt: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 7, border: '1px solid var(--glass-border)',
  background: 'var(--glass-hover)', color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: 16, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1,
};

// ── GlassDatePicker ────────────────────────────────────────────────────────────
const GlassDatePicker: React.FC<Props> = ({ value, onChange, disabled, hasError }) => {
  const [open, setOpen]     = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef   = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = triggerRef.current; if (!el) return;
    const r  = el.getBoundingClientRect();
    const pH = popupRef.current?.offsetHeight ?? 280;
    const pW = popupRef.current?.offsetWidth  ?? 248;
    let top  = r.bottom + 8;
    if (top + pH > window.innerHeight - 12) top = r.top - pH - 8;
    let left = r.left;
    if (left + pW > window.innerWidth - 12) left = window.innerWidth - pW - 12;
    setCoords({ top, left: Math.max(8, left) });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onKey  = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const onDown = (e: MouseEvent) => {
      if (popupRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, place]);

  const parsed  = parseDateStr(value);
  const display = parsed
    ? `${String(parsed.d).padStart(2,'0')} ${MONTHS_SH[parsed.mo-1]} ${parsed.y}`
    : 'DD Mon YYYY';

  return (
    <>
      <div ref={triggerRef} onClick={() => { if (!disabled) setOpen(o => !o); }} style={{
        display: 'inline-flex', alignItems: 'center', flex: 1,
        background: 'var(--input-bg)',
        border: `1px solid ${hasError ? '#f87171' : open ? 'var(--accent)' : 'var(--input-border)'}`,
        borderRadius: 8, padding: '0 12px', height: 38, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'border-color .15s',
        userSelect: 'none', WebkitUserSelect: 'none', boxSizing: 'border-box',
        minWidth: 130,
      }}>
        <span style={{ fontSize: 13, color: parsed ? 'var(--text-primary)' : 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace', letterSpacing: '.02em', whiteSpace: 'nowrap' }}>
          {display}
        </span>
        <span style={{ marginLeft: 'auto', paddingLeft: 8, fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>📅</span>
      </div>

      {open && createPortal(
        <CalPopup
          ref={popupRef}
          value={value}
          top={coords.top}
          left={coords.left}
          onPick={v => { onChange(v); setOpen(false); }}
          onClose={() => setOpen(false)}
        />,
        document.body
      )}
    </>
  );
};

export default GlassDatePicker;
